import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import { BackendService } from '../BackendService';
import { NotificationsService } from '../NotificationsService';
import { PlayerService } from '../PlayerService';
import { RpcService } from '../RpcService';
import {
	IPromoCode,
	IPromoCodeAdmin,
	IPromoService,
} from './promoServiceTypes';

export class PromoService implements IPromoService {
	private readonly promoCodes = new Subject<IPromoCode[]>([]);

	constructor(
		private readonly backend: BackendService,
		private readonly rpc: RpcService,
		player: PlayerService,
		notifications: NotificationsService,
	) {
		player.waitForCharacterSelect().then(() => {
			this.fetchPromoCodes().catch((err) => notifications.showException(err));
		});

		backend.addEventHandler('promo:invalidate', () => {
			this.fetchPromoCodes().catch((err) => notifications.showException(err));
		});
	}

	public async fetchPromoCodes() {
		const codes = await this.rpc.invokeServer<IPromoCode[]>('promo:getCodes');

		this.promoCodes.next(codes);
	}

	public getPromoCodes(): IObservable<IPromoCode[]> {
		return this.promoCodes;
	}

	@notifyOnError
	public async activateCode(code: string): Promise<void> {
		await this.backend.invoke('promo:activate', { code });
	}

	@notifyOnError
	public async claimRewards(code: string): Promise<void> {
		await this.rpc.invokeServer('promo:claim', { code });
	}

	@notifyOnError
	public async createPromoCode(payload: {
		code: string;
		dateStart: number | null;
		dateEnd: number | null;
		requiredPlayTimeMin: number;
		maxActivations: number | null;
		rewards: { type: string; value: string }[];
	}): Promise<void> {
		await this.backend.invoke('promo:create', payload);
	}

	public async fetchAllPromoCodes(): Promise<IPromoCodeAdmin[]> {
		return this.backend.invoke<IPromoCodeAdmin[]>('promo:list');
	}

	@notifyOnError
	public async deletePromoCode(code: string): Promise<boolean> {
		return this.backend.invoke<boolean>('promo:delete', { code });
	}

	public async exitMenu(): Promise<void> {
		await this.rpc.invokeClient('promo:exitMenu');
	}
}
